import { template as _$template } from "solid-js/web";
import { delegateEvents as _$delegateEvents } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { setAttribute as _$setAttribute } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<div class=flight__boot-report><pre class=flight__boot-report__text></pre><button type=button class=flight__boot-report__copy data-testid=boot-report-copy aria-label="Copy the boot report to the clipboard">`);
import { createResource, createSignal, onCleanup, onMount, Show } from "solid-js";
import { adapterLabel, bootMarks, bootReportCopyLabel, bootReportLines, bootReportText, bootReportWanted, copyBootReport } from "./boot-report.ts";
import { pipelineProbe } from "./boot-probe.ts";
import { BOOT_PHASES } from "./boot-progress.ts";
import { plannedRendererFacts } from "../three/renderer-plan.ts";
import { rendererFactLabel } from "../three/renderer-mode.ts";

/**
 * The `?boot-report` panel — `boot-report.ts`'s numbers as selectable DOM text
 * over the corner of the flight.
 *
 * **DOM text and not the bitmap plate**, for the reason `FlightStatusOverlay.tsx`
 * already gives about its own diagnostics: this is prose a player is meant to
 * read and quote back, and setting a driver string in a five-pixel font would be
 * a costume rather than a port. `pointer-events: none` so the hatch cannot get
 * between the player and the aeroplane he is still flying.
 *
 * It samples once a second rather than once, because the last three or four
 * pipelines are compiled *after* `ready` (`fly-boot-timing.md` §6e) and a report
 * taken on the frame the picture appears would print a count that is about to
 * change. It settles within a couple of seconds and then stops moving, which is
 * the moment to screenshot it.
 *
 * ## The COPY button, and the one hole it must not open
 *
 * A screenshot has to be read back by hand before any of these numbers can be
 * compared, so the user asked (2026-09-01) for the report as text. The button
 * copies `bootReportText` — the identical string the `<pre>` above it is
 * showing — and reports the *truth* about the write: `navigator.clipboard` is
 * absent outside a secure context and `writeText` rejects on a document that
 * has no permission, and a button that said COPIED over an empty clipboard
 * would cost the next slow-boot report entirely.
 *
 * The button is the only part of this panel that takes the pointer, exactly as
 * `KillCamFrame.tsx` does it: the body keeps `pointer-events: none` so a pilot
 * firing through the corner of his own screen still fires, the button alone
 * takes `auto`, and `keys.ts`'s `UI_OVERLAY_CLASSES` carries `flight__boot-report`
 * so pressing it cannot fall through and pull the trigger behind it.
 */
export function BootReportPanel() {
  const [lines, setLines] = createSignal([]);
  const [copyState, setCopyState] = createSignal("idle");
  let resetTimer;
  const [adapter] = createResource(async () => {
    const gpu = navigator.gpu;
    if (!gpu) return "";
    try {
      const info = (await gpu.requestAdapter())?.info;
      return info ? adapterLabel(info) : "";
    } catch {
      return "";
    }
  });
  const sample = () => {
    const marks = bootMarks();
    setLines(bootReportLines({
      marks,
      order: BOOT_PHASES,
      probe: pipelineProbe(marks),
      renderer: rendererFactLabel(plannedRendererFacts()),
      adapter: adapter() ?? "",
      userAgent: navigator.userAgent
    }));
  };
  onMount(() => {
    sample();
    const timer = setInterval(sample, 1000);
    onCleanup(() => {
      clearInterval(timer);
      clearTimeout(resetTimer);
    });
  });
  const copy = () => {
    const clipboard = navigator.clipboard;
    const write = clipboard ? text => clipboard.writeText(text) : undefined;
    void copyBootReport(bootReportText(lines()), write).then(state => {
      setCopyState(state);
      // Back to COPY after a beat, so the panel goes on reading as the live
      // instrument it is rather than freezing on the last thing it was told.
      clearTimeout(resetTimer);
      resetTimer = setTimeout(() => setCopyState("idle"), COPY_FEEDBACK_MS);
    });
  };
  return _$createComponent(Show, {
    get when() {
      return lines().length > 0;
    },
    get children() {
      var _el$ = _tmpl$(),
        _el$2 = _el$.firstChild,
        _el$3 = _el$2.nextSibling;
      _$insert(_el$2, () => bootReportText(lines()));
      _el$3.$$click = copy;
      _$insert(_el$3, () => bootReportCopyLabel(copyState()));
      _$effect(() => _$setAttribute(_el$3, "data-copy-state", copyState()));
      return _el$;
    }
  });
}

/** How long COPIED / COPY FAILED stays up. Long enough to be read, short enough not to hide the next press. */
const COPY_FEEDBACK_MS = 2000;

/** `?boot-report` against the live `location`, or `false` where there is none. */
export function bootReportRequested() {
  return typeof location !== "undefined" && bootReportWanted(location.search);
}
_$delegateEvents(["click"]);